import { CheckCircle2, AlertTriangle, TrendingUp, TrendingDown } from "lucide-react";
import { formatCurrency } from "@/lib/currencyUtils";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const MenuHealthDashboard = ({ menuHealth, currency, className }) => {
  const { t } = useTranslation();
  const {
    total_active_recipes,
    average_plate_profit,
    target_margin,
    is_healthy,
    artistic_count,
    engine_count
  } = menuHealth;

  const avgProfit = parseFloat(average_plate_profit);
  const progressPercent = Math.min((avgProfit / 100) * 100, 100);

  return (
    <div 
      className={cn("bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl border border-slate-700 p-6 shadow-xl", className)}
      data-testid="menu-health-dashboard"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-2xl font-bold text-white font-manrope flex items-center gap-3">
            {t("menuHealth.title")}
            {is_healthy ? (
              <CheckCircle2 className="h-7 w-7 text-green-500" data-testid="health-indicator-green" />
            ) : (
              <AlertTriangle className="h-7 w-7 text-amber-500" data-testid="health-indicator-amber" />
            )}
          </h3>
          <p className="text-slate-400 mt-1">
            {t("menuHealth.analyzing", { count: total_active_recipes })}
          </p>
        </div>
        
        {/* Status Badge */}
        <div className={cn(
          "px-4 py-2 rounded-lg border font-semibold",
          is_healthy 
            ? "bg-green-500/10 border-green-500/30 text-green-500" 
            : "bg-amber-500/10 border-amber-500/30 text-amber-500"
        )}>
          {is_healthy ? t("menuHealth.healthyMenu") : t("menuHealth.needsTweaking")}
        </div>
      </div>

      {/* Average PlateProfit % Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-slate-300 font-medium">{t("menuHealth.averagePlateProfit")}</span>
          <div className="flex items-center gap-2">
            <span className={cn(
              "text-3xl font-bold font-mono",
              is_healthy ? "text-green-500" : "text-amber-500"
            )} data-testid="average-profit-percent">
              {average_plate_profit}%
            </span>
            <span className="text-slate-500">/ {target_margin}% {t("menuHealth.target")}</span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="relative w-full h-8 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
          {/* Target Line */}
          <div 
            className="absolute top-0 bottom-0 w-0.5 bg-white z-20"
            style={{ left: `${target_margin}%` }}
          >
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-xs text-white whitespace-nowrap">
              {t("menuHealth.target")}
            </div>
          </div>
          
          {/* Progress Fill */}
          <div 
            className={cn(
              "h-full transition-all duration-500 ease-out",
              is_healthy 
                ? "bg-gradient-to-r from-green-600 to-green-500" 
                : "bg-gradient-to-r from-amber-600 to-amber-500"
            )}
            style={{ width: `${progressPercent}%` }}
            data-testid="profit-progress-bar"
          />
        </div>
      </div>

      {/* Art vs. Profit Summary */}
      <div className="grid grid-cols-2 gap-4">
        {/* Artistic Dishes (Low Margin) */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="h-5 w-5 text-amber-500" />
            <span className="text-sm text-slate-400 font-medium">{t("menuHealth.artisticDishes")}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-amber-500 font-mono" data-testid="artistic-count">
              {artistic_count}
            </span>
            <span className="text-slate-500 text-sm">{t("menuHealth.belowTarget")}</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {t("menuHealth.artisticDesc")}
          </p>
        </div>

        {/* Engine Dishes (High Margin) */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            <span className="text-sm text-slate-400 font-medium">{t("menuHealth.engineDishes")}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-green-500 font-mono" data-testid="engine-count">
              {engine_count}
            </span>
            <span className="text-slate-500 text-sm">{t("menuHealth.atOrAboveTarget")}</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {t("menuHealth.engineDesc")}
          </p>
        </div>
      </div>

      {/* Balance Insight */}
      <div className="mt-4 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
        <p className="text-sm text-slate-300">
          <span className="font-semibold text-amber-500">{t("menuHealth.balanceInsight")}</span>{" "}
          {engine_count === 0 && artistic_count > 0 && t("menuHealth.needsEngine")}
          {engine_count > 0 && artistic_count === 0 && t("menuHealth.allProfitable")}
          {engine_count > 0 && artistic_count > 0 && engine_count >= artistic_count && 
            t("menuHealth.greatBalance", { 
              engine: engine_count, 
              enginePlural: engine_count !== 1 ? 'es' : '',
              engineSupport: engine_count === 1 ? 's' : '',
              artistic: artistic_count,
              artisticPlural: artistic_count !== 1 ? 's' : ''
            })
          }
          {engine_count > 0 && artistic_count > 0 && engine_count < artistic_count && 
            t("menuHealth.tooManyArtistic", { 
              artistic: artistic_count, 
              engine: engine_count,
              enginePlural: engine_count !== 1 ? 'es' : ''
            })
          }
          {engine_count === 0 && artistic_count === 0 && t("menuHealth.addRecipes")}
        </p>
      </div>
    </div>
  );
};

export default MenuHealthDashboard;
